// Per-(encounter, map) crop configuration.
//
// A boss can span several maps (phases) and several bosses can share one map
// image with different crops, so config is keyed by (encounterId, uiMapId):
// the encounterId is ENCOUNTER_START's id (= DungeonEncounterID), the uiMapId is
// the MAP_CHANGE map active at the scrub time. The image + world bounds belong
// to the uiMapId (shared); the crop + rotation belong to the pair.
//
// The DATA is authored in the raidmapper repo (a file-based crop/rotation
// editor) and baked into a MessagePack artifact that raidlogger's build pulls
// in (see bin/pull-maps) as ./arenas.msgpack — the app reads it statically,
// with no runtime fetch. This file owns the TYPES and the `arenaFor()` seam.
//
// We do NOT infer bounds from player positions: a guessed rect is wrong whenever
// the raid doesn't traverse the whole arena (stack fights, ranged phases). A map
// with no authored config renders a placeholder rather than fake positions.
//
//  - bounds:   the full world rect the PNG covers. north=max posX, south=min posX,
//              west=max posY, east=min posY (WoW posX = N↔S = screen vertical;
//              posY = W↔E = horizontal). From MAP_CHANGE / UiMapAssignment datamine.
//  - view:     the camera authored in the map editor — where the map is centred,
//              its rotation, and zoom. The replay builds the SAME image→canvas
//              matrix from it (see lib/replay.ts viewMatrix) and draws the art and
//              the unit dots through that one matrix, so they can never desync.

export interface WorldBounds {
  north: number; // max posX
  south: number; // min posX
  west: number; // max posY
  east: number; // min posY
}

export interface View {
  centerX: number; // image point at viewport centre (0..1)
  centerY: number;
  rotation: number; // degrees
  scale: number; // zoom relative to "whole image fits" (1 = fit)
}

export interface ArenaConfig {
  bounds: WorldBounds;
  view: View;
}

// The data is a MessagePack binary authored in raidmapper and pulled in at
// build time (bin/pull-maps → ./arenas.msgpack). Vite bundles + fingerprints it
// as a static asset; we decode it once at boot — fully offline, no runtime API.
import { decode } from "@msgpack/msgpack";
import arenasUrl from "./arenas.msgpack?url";

// { [encounterId]: { [uiMapId]: ArenaConfig } }
let ARENAS: Record<number, Record<number, ArenaConfig>> = {};

/** Load + decode the baked arena data. Call once at app boot before mount. */
export async function loadArenas(): Promise<void> {
  const bytes = await (await fetch(arenasUrl)).arrayBuffer();
  ARENAS = decode(bytes) as Record<number, Record<number, ArenaConfig>>;
}

/** Authored config for an (encounter, map) pair, or undefined if unconfigured. */
export function arenaFor(encounterId: number, uiMapId: number): ArenaConfig | undefined {
  return ARENAS[encounterId]?.[uiMapId];
}
